import { useTranslation } from "react-i18next";
import React from "react";
import { Modal } from "antd";
import Table from "components/Common/dataTable";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { formatToLocaleDate } from "utils/dateUtils";
import { useAbility } from "config/abilityContext";
import { TeacherJournal } from "types/teacherJournalTypes";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { getSubjectLabelFromCode } from "utils/schoolUtils";

const { confirm } = Modal;

interface Props {
  dataSource: TeacherJournal[];
  onEdit: (record: any) => void;
  onDelete: (id: string) => void;
  loading: boolean;
  hideActions?: boolean;
  dataPaginate?: any;
  onChange?: (pg: any) => void;
}

const TeacherJournalTable: React.FC<Props> = ({
  dataSource,
  onEdit,
  onDelete,
  loading,
  hideActions = false,
  dataPaginate,
  onChange,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const columns: any[] = [
    {
      title: t("teacherJournal.form.class"),
      dataIndex: ["class", "name"],
      key: "class",
      width: "15%",
      render: (_: any, record: any) => record.class?.name || "-",
    },
    {
      title: t("teacherJournal.form.subject"),
      dataIndex: ["subject", "name"],
      key: "subject",
      width: "15%",
      render: (_: any, record: any) => getSubjectLabelFromCode(record.subject?.code) || "-",
    },
    {
      title: t("teacherJournal.form.teacher"),
      key: "teacher",
      width: "15%",
      render: (_: any, record: any) => {
        return (
          <UserProfileHeader
            photo={record.teacher?.user?.photo}
            firstName={record.teacher?.user?.firstName || ""}
            lastName={record.teacher?.user?.lastName || ""}
          />
        );
      },
    },
    {
      title: t("teacherJournal.form.date"),
      dataIndex: "date",
      key: "date",
      width: "10%",
      render: (date: string | undefined) => formatToLocaleDate(date),
    },
    {
      title: t("teacherJournal.form.session_number"),
      dataIndex: "sessionNumber",
      key: "sessionNumber",
      width: "8%",
      align: "center" as const,
      render: (sessionNumber: number | null) =>
        sessionNumber ? sessionNumber : "-",
    },
    {
      title: t("teacherJournal.form.period"),
      dataIndex: "periodLabel",
      key: "periodLabel",
      width: "10%",
      render: (periodLabel: string | null) => periodLabel || "-",
    },
    {
      title: t("teacherJournal.form.competency"),
      dataIndex: "competency",
      key: "competency",
      width: "20%",
      render: (competency: string | null) =>
        competency ? (
          <span>
            {competency.length > 80
              ? `${competency.slice(0, 80)}...`
              : competency}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: t("action"),
      dataIndex: "action",
      hidden: hideActions,
      width: "10%",
      render: (_: string, record: any) => {
        const actions: Action[] = [
          {
            label: t("edit"),
            icon: "ti ti-edit",
            actionType: "link",
            onClick: () => onEdit(record),
            hidden: !ability.can("update", "TeacherJournal") && !ability.can("read", "TeacherJournal"),
          },
          {
            label: t("delete"),
            icon: "ti ti-trash",
            actionType: "link",
            onClick: () => showDeleteConfirm(record.id),
            hidden: !ability.can("delete", "TeacherJournal") && !ability.can("read", "TeacherJournal"),
          },
        ];


        return actions.length > 0 ? <ActionsDropdown actions={actions} /> : <i className="ti ti-lock"></i>;
      },
    },
  ];

  const showDeleteConfirm = (id: string) => {
    confirm({
      title: t(
        "teacherJournal.delete_confirmation.title"
      ) as string,
      icon: <ExclamationCircleOutlined />,
      content: t("teacherJournal.delete_confirmation.description") as string,
      okText: t("delete") as string,
      okType: "danger",
      cancelText: t("cancel") as string,
      okButtonProps: { disabled: loading },
      onOk: () => onDelete(id),
    });
  };

  return (
    <div className="">
      <div className="table-responsive">
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={true}
          dataPaginate={dataPaginate}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

export default TeacherJournalTable;
